<template>
  <div class="member-profile-header">
    <!-- プロフィール画像 -->
    <b-card-img
      v-bind:src="profileImageUrl"
      class="profile-icon"
      crossorigin="use-credentials"
    />
    <div class="header-content">
      <div class="content-left" v-bind:class="{ 'no-kana': !userNameKana }">
        <!-- ユーザー名 -->
        <span class="user-name">
          {{ userName }}
        </span>
        <!-- ユーザー名フリガナ -->
        <span class="user-name-kana" v-if="userNameKana">
          {{ userNameKana }}
        </span>
      </div>
      <div class="content-right">
        <!-- 最終更新日時 -->
        <div class="event-time">
          {{ lastPositioningAtText }}
        </div>
        <!-- アクション -->
        <b-button
          v-if="isMessageAvailable"
          v-ifield-b-tooltip.hover
          class="action-btn message-btn"
          size="sm"
          variant="light"
          v-bind:title="$t('appMemberProfileHeader.action.createMessage')"
          v-on:click="$_emitMessageClickEvent"
        >
          <message-icon class="icon" />
        </b-button>
      </div>
    </div>
  </div>
</template>

<script>
import { FUNCTION_TYPE } from '@/constants.js'
import { inject, computed } from 'vue'
import { RestApiClient, TAG_USER_ROLE_ID } from 'ifield-core-components'
import MessageIcon from '@/assets/ifield/ic_message.svg?component'
import { useDateDisplayText } from '@/composables/UseDateDisplayText'

export default {
  name: 'AppMemberProfileHeader',
  components: {
    'message-icon': MessageIcon,
  },
  inheritAttrs: false,
  props: {
    /**
     * ユーザーデータ
     */
    data: {
      type: Object,
      default: null,
    },
  },
  setup(props) {
    const lastPositioningAt = computed(() => props.data?.last_positioning_at)
    const baseDate = inject('baseDate')
    // 最終測位時刻の表示テキスト
    const { text } = useDateDisplayText({
      date: lastPositioningAt,
      baseDate,
    })
    return { lastPositioningAtText: text }
  },
  computed: {
    /**
     * ユーザーID
     * @type {Number}
     */
    userId: function () {
      return this.data && this.data.id
    },
    /**
     * ユーザー名
     * @type {String}
     */
    userName: function () {
      return this.data && this.data.user_name
    },
    /**
     * ユーザー名フリガナ
     * @type {String}
     */
    userNameKana: function () {
      return this.data && this.data.user_name_kana
    },
    /**
     * ユーザープロフィール画像の添付ファイルタイプ
     * @type {String}
     */
    profileImageAttachFileType: function () {
      return this.data && this.data.profile_image
        ? this.data.profile_image.attach_file_type
        : null
    },
    /**
     * プロフィール画像のURL
     * @type {String}
     */
    profileImageUrl: function () {
      let url = RestApiClient.createApiUrl('download_file')
      let params = url.searchParams
      if (this.userId) {
        params.append('id', this.userId)
      }
      params.append('thumbnail', 1)
      if (this.profileImageAttachFileType) {
        params.append('attach_file_type', this.profileImageAttachFileType)
      }
      return url.toString()
    },
    /**
     * メッセージが利用可能か？
     * タグユーザーはメッセージを送信できない
     * @type {Boolean}
     */
    isMessageAvailable: function () {
      return (
        this.roleId !== TAG_USER_ROLE_ID &&
        !!this.$store.getters.functionType(FUNCTION_TYPE.SEND_MESSAGE)
      )
    },
  },
  methods: {
    /**
     * メッセージボタンクリックイベント発行
     */
    $_emitMessageClickEvent: function () {
      /**
       * メッセージボタンクリックイベント
       */
      this.$emit('message-click')
    },
  },
}
</script>

<style lang="scss" scoped>
.member-profile-header {
  width: 100%;
  height: 56px;
  background-color: #f0f0f0;
  padding: 10px 22px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  column-gap: 8px;
}

.profile-icon {
  display: block;
  border: 1px solid #eeeeee;
  border-radius: 50%;
  width: 40px !important;
  height: 40px !important;
}

.header-content {
  width: 100%;
  height: 100%;
  margin: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: nowrap;
}

.content-left {
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  row-gap: 4px;
  padding-top: 4px;

  &.no-kana {
    padding-top: 0;
  }
}

.user-name {
  color: #2c3e50;
  font-size: $font-size-large;
  font-weight: $font-weight-bold;
  line-height: 1;
  letter-spacing: 0.01rem;
}

.user-name-kana {
  color: #989898;
  font-size: $font-size-small;
  line-height: 1;
}

.content-right {
  height: 100%;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex-wrap: nowrap;
  column-gap: 16px;
}

.event-time {
  color: #989898;
  font-size: $font-size-small;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  column-gap: 8px;
  white-space: nowrap;
}

.action-btn {
  @include mixin-button(true);
  & {
    border-radius: 4px;
    border: 1px solid #cbd1d8;
  }
}
</style>
